import { ClientEvents } from 'common/events/ClientEvents';
import { Slugs } from 'common/urls/Slugs';
import React, { useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import closeIconSrc from 'src/assets/svg/design-library/close-dark-grey-small.svg';
import { useChatContext } from 'src/chat/hooks/useChatContext';
import { isNativeMobileApp } from 'src/mobile-app/isNativeMobileApp';
import { useMealPlanSurfacingLogic } from 'src/pages/consumer/meal-plans/hooks/useMealPlanSurfacingLogic';
import { LinkButton } from 'src/shared/design-system/Atoms';
import { LoginLink } from 'src/shared/design-system/Buttons/LoginLink';
import { useFeatureFlag } from 'src/shared/design-system/FeatureFlag';
import { Shef } from 'src/shared/design-system/Icon/Shef';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { MealPlanSubscriptionContext } from '../../../meal-plans/MealPlanSubscriptionsContext';
import { ZipCodeSelectionModalContext } from '../../header-v2/ZipCodeSelection/ZipCodeSelectionModalContext';
import {
  getMenuItems,
  LOGGED_IN_LOWER_SECTION_MENU_ITEMS,
  LOGGED_OUT_LOWER_SECTION_MENU_ITEMS,
  MenuItemType,
  MIDDLE_SECTION_MENU_ITEMS,
  NATIVE_APP_LINKS,
  TOP_SECTION_MENU_ITEMS,
} from './utils';

interface AccountSettingsModalContentProps {
  /** Function to dismiss the modal */
  hide: () => void;
  onLoginClick: React.MouseEventHandler;
  onLogOutClick: React.MouseEventHandler;
}

export const AccountSettingsModalContent: React.FC<AccountSettingsModalContentProps> = ({
  onLoginClick,
  onLogOutClick,
  hide,
}) => {
  const { shouldShowMealPlanButton, mealPlanIntakeRouteByRegion } = useMealPlanSurfacingLogic();
  const { showZipCodeModal } = useContext(ZipCodeSelectionModalContext);
  const location = useLocation();
  const tracker = useTracker();
  const shouldRedirect = locationInRoutes(location, [
    Routes.CONSUMER_EXPLORE,
    Routes.CONSUMER_ORDER_SEARCH,
    Routes.CONSUMER_ORDER_ONE_TIME,
    Routes.CONSUMER_MEAL_PLANS_PREFERENCES,
    Routes.CONSUMER_MEAL_PLANS_CHECKOUT,
  ]);
  const [currentUser] = useCurrentUser();
  const { unread } = useChatContext();
  const [cachedZipCode] = useCachedZipCode();
  const history = useHistory();
  const { userHasActiveMealPlan } = useContext(MealPlanSubscriptionContext);
  const showManageMealPlans = useFeatureFlag('showManageMealPlans');

  const updateZipCode = () => {
    hide();
    const isMealPlan =
      location.pathname.includes(Routes.CONSUMER_MEAL_PLANS_PREFERENCES) ||
      location.pathname.includes(Routes.CONSUMER_MEAL_PLANS_CHECKOUT);
    showZipCodeModal({ isMealPlan, redirectOnSubmit: shouldRedirect });
  };

  const getMenuItemsForSection = (items: MenuItemType[]) =>
    getMenuItems(
      items,
      currentUser,
      hide,
      unread,
      updateZipCode,
      cachedZipCode,
      showManageMealPlans,
      userHasActiveMealPlan
    );

  const handleMealPlanClick = useCallback(() => {
    const elementName = userHasActiveMealPlan ? 'review-upcoming-meals' : 'build-meal-plan';
    const redirectUrl = userHasActiveMealPlan ? Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER : mealPlanIntakeRouteByRegion;

    tracker.track(ClientEvents.NAV_CLICK, { elementName });
    if (!cachedZipCode && !userHasActiveMealPlan) {
      showZipCodeModal({ isMealPlan: true, redirectOnSubmit: true });
      hide();
    } else {
      history.push(redirectUrl);
      hide();
    }
  }, [cachedZipCode, tracker, userHasActiveMealPlan, showZipCodeModal, hide, history, mealPlanIntakeRouteByRegion]);

  const handleExploreClick = () => {
    tracker.track(ClientEvents.NAV_CLICK, { elementName: 'explore-local-shefs' });
    if (!cachedZipCode) {
      showZipCodeModal({ redirectOnSubmit: true });
    } else {
      const exploreUrl = Routes.CONSUMER_EXPLORE.replace(`:${Slugs.ZIP_CODE}`, cachedZipCode ?? '');
      hide();
      history.push(exploreUrl);
    }
  };

  const bottomMenuItems = [
    ...(currentUser ? LOGGED_IN_LOWER_SECTION_MENU_ITEMS : LOGGED_OUT_LOWER_SECTION_MENU_ITEMS),
    ...(isNativeMobileApp() ? NATIVE_APP_LINKS : []),
  ];

  const mealPlanButton = useMemo(
    () =>
      !shouldShowMealPlanButton ? null : (
        <Button buttonType={ButtonType.Primary} onClick={handleMealPlanClick}>
          {userHasActiveMealPlan ? 'Review upcoming delivery' : 'Set up your Weekly Shef Service'}
        </Button>
      ),
    [userHasActiveMealPlan, handleMealPlanClick, shouldShowMealPlanButton]
  );

  const divider = <div className='my-4 h-0 w-full border-t border-solid border-t-neutral-200' />;

  return (
    <div className='flex size-full max-h-svh flex-col md:max-h-[95vh]'>
      <div className='flex w-full items-center justify-between border-b border-solid border-b-neutral-100 px-6 py-3'>
        <button className='border-none bg-white' onClick={hide}>
          <img src={closeIconSrc} style={{ width: 12, height: 12 }} alt='close icon' />
        </button>
        <Link to={Routes.CONSUMER_LANDING}>
          <Shef className='text-raspberry' height='24px' />
        </Link>
      </div>
      <div className='size-full overflow-y-scroll px-6 pt-4'>
        {mealPlanButton}

        {cachedZipCode && (
          <div className='my-4'>
            <Button buttonType={ButtonType.Secondary} onClick={handleExploreClick}>
              Explore local shefs
            </Button>
          </div>
        )}

        {!currentUser && (
          <div className='my-4 max-w-[400px] text-center'>
            <LoginLink onClick={onLoginClick} text='Log in or Sign Up' />
          </div>
        )}

        {currentUser && (
          <>
            {getMenuItemsForSection(TOP_SECTION_MENU_ITEMS)}
            {divider}
            {getMenuItemsForSection(MIDDLE_SECTION_MENU_ITEMS)}
            {divider}
          </>
        )}
        {getMenuItemsForSection(bottomMenuItems)}
      </div>
      {currentUser && (
        <div className='flex w-full flex-col items-center justify-center gap-2 rounded-b-lg border-t border-t-neutral-100 bg-white py-4'>
          <span className='text-lg leading-6'>{currentUser.fullName}</span>
          <span className='text-sm leading-5'>{currentUser.email}</span>
          <LinkButton onClick={onLogOutClick}>Log out</LinkButton>
        </div>
      )}
    </div>
  );
};
